import React, { useMemo, useEffect, useState } from 'react';

import Table from '@awp/components/table';

import { Button, Row, Col, FormLabel, FormCheck } from 'react-bootstrap';
import { FORM_MODE, Permissions } from '@awp/constants';
import { hasAccess } from '@components/Authorize';

import AwpYesNoModal from '@components/Common/AwpYesNoModal';
import AwpDropdown from '@awp/components/Common/DropDown/AwpDropdown';
import { isValueEmpty } from '@awp/helpers/utilsReact';

import { useNotificationContext } from './NotificationsContext';
import { useNotificationTableFilterContext } from './NotificationTableFilterContext';
import {
  //actionMenu,
  activeTypeDropdown,
  alertTypeDropdown,
  formatOptionLabel,
  handleDelete,
} from './NotificationCommon';
import { getTableColumns } from './NotificationTableColumns';
import { Crud } from '@awp/shared/Crud';

/**
 * NotificationTable component renders a table of notifications with filters and controls.
 *
 * @returns {JSX.Element} The NotificationTable component.
 */
const NotificationTable = () => {
  // Extracting values from NotificationContext
  const context = useNotificationContext();
  const {
    data,
    setData,
    search,
    setFilteredData,
    setMode,
    mode,
    notificationId,
    setNotificationId,
    loading,
    breakpoints,
    setSearch,
    filteredData,
    currentUser,
    showYesNoModal,
    setShowYesNoModal,
  } = context;

  const { alertType, setAlertType, activeType, setActiveType, subscribedTo, setSubscribedTo } =
    useNotificationTableFilterContext();

  const crud = new Crud('Alerts', mode);

  // const [alertType, setAlertType] = useState({ value: '', label: 'All' }); // State for alert type filter
  // const [activeType, setActiveType] = useState({ value: '', label: 'All' }); // State for active type filter
  // const [subscribedTo, setSubscribedTo] = useState(false); // State for subscribed alerts filter

  /**
   * Checks if a search term is found within a source string.
   *
   * @param {string} src - The source string to search within.
   * @returns {boolean} True if the search term is found, otherwise false.
   */
  const hasSearch = (src) => {
    return src ? src.toLowerCase().includes(search.toLowerCase()) : false;
  };

  // Filters the data based on alert type, active type, search, and subscribedTo flags
  useEffect(() => {
    let filtered = data.filter((x) => x.IsDeleted !== 'Y');

    if (alertType && alertType.value !== '') filtered = filtered.filter((x) => x.IsPrivate === alertType.value);

    if (activeType && activeType.value !== '') filtered = filtered.filter((x) => x.IsActive === activeType.value);

    if (!isValueEmpty(search)) {
      filtered = filtered.filter((x) => hasSearch(x.WeatherStationDisplayNames) || hasSearch(x.Name));
    }

    if (subscribedTo) {
      filtered = filtered.filter((x) => x.SubscriberIds.includes(currentUser.SecurityIdentifierId));
    }
    setFilteredData(filtered);
  }, [data, alertType, activeType, search, subscribedTo]);

  // Memoized table columns, recalculated only when breakpoints or data change
  const TableColumns = useMemo(() => {
    return getTableColumns(context);
  }, [context, breakpoints, data]);

  return (
    <>
      <div className="d-flex">{crud.getHeader()}</div>
      <div className="container mb-3">
        <div className="row">
          <div className="col-sm-3">
            <FormLabel>
              <b>Search All</b>
            </FormLabel>
            <input
              className="form-control"
              placeholder="Search..."
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            ></input>
          </div>
          <div className="col-sm-3">
            <AwpDropdown
              isMulti={false}
              selectedValues={alertType}
              onChange={setAlertType}
              data={alertTypeDropdown}
              title="Alert Type"
              formatOptionLabel={formatOptionLabel}
            />
          </div>
          <div className="col-sm-3">
            <AwpDropdown
              isMulti={false}
              selectedValues={activeType}
              onChange={setActiveType}
              data={activeTypeDropdown}
              title="Active Type"
            />
          </div>
          <div className="col-sm-3">
            <FormLabel></FormLabel>
            <FormCheck
              type="checkbox"
              label="Alerts I'm subscribed to"
              checked={subscribedTo}
              onChange={() => setSubscribedTo(!subscribedTo)}
            />
          </div>
        </div>
      </div>
      <Table
        columns={TableColumns}
        data={filteredData}
        fetchData={() => {}}
        loading={loading}
        search=""
        filters={[]}
        dataTriggers={[]}
        defaultSortBy={'Name'}
      />
      {hasAccess([Permissions.WritePersonalAlert]) && (
        <div className="my-3">
          <Row>
            <Col sm={3} md={4}>
              <Button
                className="mr-2 my-3"
                variant="primary"
                onClick={() => {
                  setMode(FORM_MODE.CREATE);
                }}
                disabled={loading}
              >
                Create
              </Button>
            </Col>
          </Row>
        </div>
      )}
      <AwpYesNoModal
        showYesNoModal={showYesNoModal}
        setShowYesNoModal={setShowYesNoModal}
        onClickYes={() => {
          handleDelete(context);
        }}
        title="Delete Alert"
        body="Are you sure to delete this alert? Deleted alert can't be recovered."
      />
    </>
  );
};

export default NotificationTable;
